/**
 * Anthropic Admin API Key storage.
 */
import { STORAGE_VERSION, StoredApiKey, sanitizeApiKey, encode, decode } from './internal';
import { markAdminCredentialChanged } from './admin-credential-revision';

export const ANTHROPIC_ADMIN_API_KEY_STORAGE_KEY = 'anthropic_admin_api_key';

/**
 * Save Anthropic Admin API Key
 */
export const saveAnthropicApiKey = (apiKey: string): boolean => {
  try {
    if (!apiKey || typeof apiKey !== 'string') {
      throw new Error('Invalid API key');
    }
    const normalizedApiKey = sanitizeApiKey(apiKey);
    if (!normalizedApiKey) {
      throw new Error('Invalid API key');
    }

    const data: StoredApiKey = {
      value: encode(normalizedApiKey),
      createdAt: Date.now(),
      version: STORAGE_VERSION
    };

    localStorage.setItem(ANTHROPIC_ADMIN_API_KEY_STORAGE_KEY, JSON.stringify(data));
    markAdminCredentialChanged();
    return true;
  } catch (error) {
    console.error('Failed to save Anthropic API key:', error);
    return false;
  }
};

/**
 * Get Anthropic Admin API Key
 */
export const getAnthropicApiKey = (): string | null => {
  try {
    // First, check environment variable
    if (import.meta.env.VITE_ANTHROPIC_ADMIN_API_KEY) {
      return import.meta.env.VITE_ANTHROPIC_ADMIN_API_KEY;
    }

    // Fall back to local storage
    const stored = localStorage.getItem(ANTHROPIC_ADMIN_API_KEY_STORAGE_KEY);
    if (!stored) return null;

    const data: StoredApiKey = JSON.parse(stored);

    if (data.version !== STORAGE_VERSION) {
      removeAnthropicApiKey();
      return null;
    }

    const decoded = decode(data.value);
    if (!decoded) return null;
    return sanitizeApiKey(decoded) || null;
  } catch (error) {
    console.error('Failed to get Anthropic API key:', error);
    localStorage.removeItem(ANTHROPIC_ADMIN_API_KEY_STORAGE_KEY);
    markAdminCredentialChanged();
    return null;
  }
};

/**
 * Remove Anthropic Admin API Key
 */
export const removeAnthropicApiKey = (): boolean => {
  try {
    localStorage.removeItem(ANTHROPIC_ADMIN_API_KEY_STORAGE_KEY);
    markAdminCredentialChanged();
    return true;
  } catch (error) {
    console.error('Failed to remove Anthropic API key:', error);
    return false;
  }
};

/**
 * Check if Anthropic Admin API Key exists
 */
export const hasAnthropicApiKey = (): boolean => {
  if (import.meta.env.VITE_ANTHROPIC_ADMIN_API_KEY) {
    return true;
  }
  return getAnthropicApiKey() !== null;
};
